/**
 * Shared session lifecycle helpers:
 * - endSession: teardown, used by every place that detects an expired/invalid
 *   session or handles a manual logout, so the LogOut + toast + redirect sequence
 *   isn't reimplemented (and subtly diverging) at each call site.
 * - startSessionKeeper/stopSessionKeeper: background keep-alive, proactively
 *   re-validating the session on an interval so an active-but-not-navigating
 *   user isn't silently logged out. This is a separate concern from
 *   GetPrincipal's per-navigation cache check in the router guard, which only
 *   re-validates when the user actually navigates.
 */

import store from '@/store'
import { toast } from '@/utils/toast'
import { redirectToLogin, isOnLoginPage } from '@/utils/login'
import { isNullOrEmpty } from '@/utils/common'
import { LOGIN_CACHE_TIMEOUT, MAX_USER_IDEL_TIME, SESSION_REFRESH_INTERVAL } from '@/utils/constants'

/** 
 * Show session end toast condition
 * 1. Only when a message was given
 * 2. User NOT already on the login page
 * 3. User actually had a session to end (avoids alarming a never-authenticated visitor).
*/
const showToastOnExpiry = (toastMessage) => {
    // 1. Only when a message was given
    if (!toastMessage) return false

    // 2. User NOT already on the login page
    if (isOnLoginPage()) return false

    // 3. User actually had a session to end (avoids alarming a never-authenticated visitor).
    return !isNullOrEmpty(store.state.principalState)
}

/**
 * Tear down the client session and send the user back to the login page.
 * Waits for LogOut to settle (success or failure) before redirecting, so the
 * server-side logout call isn't aborted mid-flight by the navigation.
 * @param {Object} [options]
 * @param {string} [options.toastMessage] - Warning toast to show before redirecting
 * @returns {Promise<void>}
 */
export const endSession = async ({ toastMessage } = {}) => {
    if (showToastOnExpiry(toastMessage)) {
        toast.warning(toastMessage)
    }
    try {
        await store.dispatch('LogOut')
    } catch (error) {
        console.warn('[session] LogOut failed', error)
    } finally {
        redirectToLogin()
    }
}

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart']

let sessionRefreshTimer = null
let lastActivityTime = Date.now()
const recordActivity = () => { lastActivityTime = Date.now() }

/**
 * Start the background session keep-alive. While running, periodically calls
 * RefreshPrincipal as long as the user was active recently, so a long-lived but
 * active tab doesn't silently expire between navigations.
 */
export const startSessionKeeper = () => {
    lastActivityTime = Date.now()
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, recordActivity, { passive: true }))

    sessionRefreshTimer = setInterval(async () => {
        // Only refresh if the user was active within the specific time range to prevent unnecessary refreshes when idle.
        if (Date.now() - lastActivityTime > (MAX_USER_IDEL_TIME - LOGIN_CACHE_TIMEOUT)) {
            return
        }
        const isAuthenticated = await store.dispatch('RefreshPrincipal')
        // if session expired, do not logout automatically, stay on current page
        if (!isAuthenticated) {
            stopSessionKeeper()
            console.warn('User is no longer authenticated.')
        }
    }, SESSION_REFRESH_INTERVAL)
}

/**
 * Stop the background session keep-alive and remove its activity listeners.
 */
export const stopSessionKeeper = () => {
    clearInterval(sessionRefreshTimer)
    sessionRefreshTimer = null
    ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, recordActivity))
}
